import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ToastContextType {
  show: (content: string, duration?: number) => void;
  hide: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState('');
  const [mounted, setMounted] = useState(false);
  const timerRef = useRef<any>(null);

  // 确保只在客户端挂载后才渲染Portal，避免hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  const show = (msg: string, duration = 2000) => {
    // 1. 如果有旧的定时器，先清除，防止旧的关闭逻辑影响新的
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // 2. 设置内容并显示
    setContent(msg);
    setVisible(true);

    // 3. 开启新的定时器自动关闭
    timerRef.current = setTimeout(() => {
      setVisible(false);
      timerRef.current = null;
    }, duration);
  };

  const hide = () => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <ToastContext.Provider value={{ show, hide }}>
      {children}
      {/* SSR兼容性：使用mounted状态确保服务端和客户端渲染一致 */}
      {mounted && createPortal(
        <div className={`toast-wrapper ${visible ? 'visible' : ''}`}>
          <div className="toast-content">
            {content}
          </div>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};
